import { computed, effect, EnvironmentInjector, inject, Injectable, Signal, signal } from '@angular/core';
import { UserStore } from '../stores/user.store';
import { RolePermissionType } from '../types/role.interface';
import { AssetContextService } from './asset-context.service';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { UserImpersonationService } from './user-impersonation.service';

@Injectable({ providedIn: 'root' })
export class PermissionFacadeService {
  private permissionService = inject(PermissionService);
  private authService = inject(AuthService);
  private assetContextService = inject(AssetContextService);
  private impersonationService = inject(UserImpersonationService);
  private userStore = inject(UserStore);
  private rootInjector = inject(EnvironmentInjector);

  private permissionCache = new Map<string, Signal<RolePermissionType>>();

  private getCurrentEffectiveUserId(): number | null {
    const realUserId = this.authService.currentUserId();
    return this.impersonationService.getEffectiveUserId(realUserId);
  }

  getPermissionTypeForRoute(route: string): Signal<RolePermissionType> {
    const cached = this.permissionCache.get(route);
    if (cached) return cached;
    const created = this.createPermissionSignalForRoute(route);
    this.permissionCache.set(route, created);
    return created;
  }

  private createPermissionSignalForRoute(route: string): Signal<RolePermissionType> {
    const permissionSignal = signal<RolePermissionType>(RolePermissionType.None);

    effect(
      () => {
        const assetId = this.assetContextService.getCurrentAssetId();
        const userId = this.getCurrentEffectiveUserId();

        if (!userId || !assetId) {
          permissionSignal.set(RolePermissionType.None);
          return;
        }

        this.userStore.byId()(userId);

        this.permissionService
          .getPermissionTypeForRoute(userId, assetId, route)
          .then(type => {
            if (this.assetContextService.getCurrentAssetId() === assetId && this.getCurrentEffectiveUserId() === userId) {
              permissionSignal.set(type);
            }
          })
          .catch(() => permissionSignal.set(RolePermissionType.None));
      },
      { injector: this.rootInjector },
    );

    return permissionSignal.asReadonly();
  }

  hasWriteAccess(route: string): Signal<boolean> {
    const permissionType = this.getPermissionTypeForRoute(route);
    return computed(() => permissionType() === RolePermissionType.Write);
  }

  hasReadAccess(route: string): Signal<boolean> {
    const permissionType = this.getPermissionTypeForRoute(route);
    return computed(() => permissionType() >= RolePermissionType.ReadOnly);
  }
}
